import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ME_QUERY, MY_ONBOARDING_IMAGES_READINESS } from '../../../graphql/queries';
import { fillCopy } from '@arthelper/shared/acquisition/strategy';
import { useDoneConfetti } from '../useDoneConfetti';
import { WAITLIST_URL, townSquareUrl, useWaitingDoneConversions, type useWaiting } from '../useWaiting';

type Access = NonNullable<ReturnType<typeof useWaiting>['access']>;

/**
 * The end of the waiting wizard. Their profile is built but private, so this is not the
 * live-profile Done: it says the work is in front of us and that we'll tell them when they're in
 * (Raya and The League both end an application on "we'll let you know"), and keeps the one thing
 * that gets them in sooner — their invite link — in reach.
 */
export default function WaitingDone({ access }: { access: Access }) {
  const { t } = useTranslation('welcome-onboarding');
  const [copied, setCopied] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  useDoneConfetti();
  useWaitingDoneConversions();
  const link = townSquareUrl(`/invite?invite=${access.referralHash}`);
  const needed = Math.max(1, (access.waitlist?.requiredReferrals ?? 1) - (access.waitlist?.qualifiedCount ?? 0));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="ob-done-root" data-testid="waiting-done">
      <div className="ob-done-dot-grid" />
      <div className="ob-done-card">
        <p className="ob-done-label">{t('waiting.label')}</p>
        <h1 className="ob-done-title">{fillCopy(access.copy.waitingDoneHeadline ?? '', {})}</h1>
        <p className="ob-done-subtitle">{fillCopy(access.copy.waitingDoneBody ?? '', {})}</p>

        <div className="ob-waiting-invite">
          <p className="ob-waiting-invite-title">{t('waiting.sooner_title')}</p>
          <p className="ob-waiting-invite-body">{t('waiting.sooner_body', { count: needed })}</p>
          <div className="ob-waiting-invite-row">
            <span className="ob-waiting-invite-link">{link.replace(/^https?:\/\//, '')}</span>
            <button type="button" className="ob-done-btn-primary" onClick={copy}>
              {copied ? t('waiting.copied') : t('waiting.copy')}
            </button>
          </div>
        </div>

        <div className="ob-done-actions">
          <a className="ob-waiting-cta" href={WAITLIST_URL()}>
            {t('waiting.to_waitlist')}
            <span aria-hidden>→</span>
          </a>
          <button type="button" className="ob-waiting-preview-toggle" onClick={() => setPreviewing((on) => !on)}>
            {t(previewing ? 'waiting.hide_preview' : 'waiting.preview_profile')}
          </button>
          {previewing ? <ProfilePreview /> : null}
        </div>
      </div>
    </div>
  );
}

/** Most pieces the preview shows: enough to feel like a gallery, few enough to stay in the card. */
const PREVIEW_PIECES = 6;

/**
 * What they just built, shown back to them. Their profile is private while they wait (the real
 * profile page is behind the gate), so this renders it here from their own account.
 */
function ProfilePreview() {
  const { t } = useTranslation('welcome-onboarding');
  const { data: me } = useQuery(ME_QUERY);
  const { data: images } = useQuery(MY_ONBOARDING_IMAGES_READINESS, { fetchPolicy: 'network-only' });
  const user = me?.user;
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const avatar = user?.profile?.avatarUrl as string | undefined;
  const pieces = (
    (images?.myOnboardingImagesReadiness ?? []) as {
      imageId: string;
      thumbnailUrl?: string | null;
      optimizedUrl?: string | null;
    }[]
  )
    .map((image) => ({ id: image.imageId, src: image.thumbnailUrl || image.optimizedUrl }))
    .filter((image) => Boolean(image.src))
    .slice(0, PREVIEW_PIECES);

  return (
    <div className="ob-waiting-preview" data-testid="waiting-profile-preview">
      <div className="ob-waiting-preview-head">
        {avatar ? <img className="ob-waiting-preview-avatar" src={avatar} alt="" /> : null}
        {name ? <p className="ob-waiting-preview-name">{name}</p> : null}
      </div>
      {user?.profile?.bio ? <p className="ob-waiting-preview-bio">{user.profile.bio}</p> : null}
      {pieces.length > 0 ? (
        <div className="ob-waiting-preview-grid">
          {pieces.map((piece) => (
            <img key={piece.id} src={piece.src!} alt="" loading="lazy" />
          ))}
        </div>
      ) : null}
      <p className="ob-waiting-preview-private">{t('waiting.preview_private')}</p>
    </div>
  );
}
